import { OverflowMenu, OverflowMenuItem } from "@carbon/react";
import { TableColumns } from "@flaminem/ui-components";
import React from "react";

import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { FixedTable } from "src/components/Tables/FixedTable";
import { useGetOperatorLabel } from "src/hooks/useGetOperatorLabel.ts";
import { useT } from "src/internationalization";

import { SecondaryCriteria } from "./SecondaryCriteria.types";

import "./SecondaryCriteriaTable.module.scss";

type SecondaryCriteriaTableProps = {
  data: SecondaryCriteria[];
  onEdit?: (criteria: SecondaryCriteria) => void;
  onDelete?: (id: string) => void;
};

export const SecondaryCriteriaTable: React.FC<SecondaryCriteriaTableProps> = ({
  data,
  onEdit,
  onDelete,
}) => {
  const t = useT();
  const getOperatorLabel = useGetOperatorLabel();
  const { pageSize } = useDimensions();

  const columns: TableColumns<SecondaryCriteria> = {
    property: {
      width: 1,
      flexGrow: 1,
      label: t("page.rules.modal.create.linkedCase.form.secondary.criteria.table.column.property"),
      render: (row) => `${row.secondProperty.prefix} - ${row.secondProperty.propertyName}`,
    },
    operator: {
      width: 1,
      flexGrow: 1,
      label: t("page.rules.modal.create.linkedCase.form.secondary.criteria.table.column.operator"),
      render: (row) => row.operator?.code && getOperatorLabel(row.operator?.code),
    },
    value: {
      width: 1,
      flexGrow: 1,
      label: t("page.rules.modal.create.linkedCase.form.secondary.criteria.table.column.value"),
      render: (row) => {
        if (row.operator?.operatorValueType === "sameAsCurrent") {
          return t(
            "page.rules.modal.create.linkedCase.form.secondary.criteria.table.column.value.sameAsCurrent"
          );
        }
        if (row.operator?.operatorValueType === "none") {
          return "-";
        }
        return row.value;
      },
    },
    actions: {
      width: 1,
      flexGrow: 1,
      label: "",
      render: (row) =>
        onEdit || onDelete ? (
          <div>
            <OverflowMenu flipped={true}>
              {onEdit && (
                <OverflowMenuItem
                  itemText={<div>{t("general.actions.edit")}</div>}
                  onClick={() => onEdit(row)}
                />
              )}
              {onDelete && (
                <OverflowMenuItem
                  itemText={<div>{t("general.actions.delete")}</div>}
                  onClick={() => onDelete(row.id)}
                />
              )}
            </OverflowMenu>
          </div>
        ) : (
          ""
        ),
    },
  };

  return (
    <FixedTable
      id="secondary-criteria"
      mode="inline"
      data={data}
      columns={columns}
      isLoading={false}
      pageSize={pageSize}
    />
  );
};
